import { useBrandConfig } from '~/config/branding';
import { faqContentForBrand } from './faqContent';
import { FAQ_LAYOUT_CLASSES, type FaqLayout } from './faqLayout';
import { FaqSectionView } from './FaqSectionView';
import { FaqSideNav } from './FaqSideNav';

// [MCV-1501] The knowledge base is brand-split: the HLX site answers questions
// about the Exchange, the MCV site answers questions about credentialing. The
// heading and blurb move with the content, so neither site advertises the
// other's material.
const COPY = {
  mcv: {
    // [MCV-1490] Subject line lists MedCredVault before HealthcareLX.
    title: 'MedCredVault Knowledge Base',
    blurb:
      "A complete reference for providers and healthcare organizations — credentialing, licensing, CME, compensation, contracts, document management, and the platform's AI and workforce-analytics workflows. Use the navigation to jump to any topic.",
  },
  hlx: {
    title: 'HealthcareLX Knowledge Base',
    blurb:
      'A complete guide to the Healthcare Labor Exchange — how the Exchange works, standardized labor contracting, and what participation looks like for healthcare professionals, organizations, physician groups, staffing agencies, and workforce partners. Use the navigation to jump to any topic.',
  },
} as const;

/**
 * `layout` picks the scroll geometry: `public` (the default, what `/faq` renders) scrolls the
 * document under the fixed site header, `app` (the signed-in header ?) scrolls inside Layout's
 * column. The content, order and navigation are identical. [MCV-1724]
 */
export const FAQ = ({ layout = 'public' }: { layout?: FaqLayout } = {}) => {
  const { brand } = useBrandConfig();
  const classes = FAQ_LAYOUT_CLASSES[layout];
  const { sections, sectionById, sectionOrder, navGroups } = faqContentForBrand(brand);
  const copy = COPY[brand];
  const orphanedSections = sections.filter((section) => !sectionOrder.includes(section.id));

  return (
    <main className={classes.main}>
      <section className={classes.hero}>
        <div className={classes.heroInner}>
          <p className="text-base/7 font-semibold uppercase tracking-normal text-primary-50">
            Frequently asked questions
          </p>
          <h1 className="mt-2 text-[28px] leading-tight font-semibold tracking-tight text-pretty text-grey-100 sm:text-5xl sm:leading-none">
            {copy.title}
          </h1>
          <p className="mt-3 text-base leading-6 text-grey-300 sm:mt-6 sm:text-lg sm:leading-8">
            {copy.blurb}
          </p>
        </div>
      </section>

      <div className={classes.body}>
        <div className="lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-x-12">
          {/* `contents` on mobile so the sticky dropdown inside inherits this
              tall block as its containing block. As a plain block the aside is
              only as tall as the nav itself, which left `position: sticky` with
              no room to travel — it scrolled away and never came back on a
              40,000px page. On `lg` the aside becomes a real grid item again,
              which is what gives the desktop sidebar its travel. */}
          <aside className="contents lg:block lg:col-start-1">
            <FaqSideNav
              navGroups={navGroups}
              sectionById={sectionById}
              sectionOrder={sectionOrder}
              layout={layout}
            />
          </aside>

          {/* [MCV-1592] Grouped rather than one flat run of sections, so each
              group's explanatory blurb has somewhere to live in the main
              column. Those blurbs are prose -- on HLX several run to a full
              paragraph -- and they used to render inside the navigation, where
              they read as an unscannable wall next to the links they were
              meant to introduce.

              The nav groups stay the single source of order: this walks the
              same groups in the same sequence, so anchors, the scroll-spy and
              the flattened `sectionOrder` all keep agreeing with what is on
              screen. */}
          <div className="mt-8 space-y-16 lg:col-start-2 lg:mt-0">
            {navGroups.map((group) => {
              const groupSections = group.sectionIds
                .map((id) => sectionById[id])
                .filter((section): section is (typeof sections)[number] => Boolean(section));
              if (!groupSections.length) return null;

              return (
                <section key={group.id} aria-labelledby={`${group.id}-heading`}>
                  {/* [MCV-1660] The "start-here" section this group replaced
                      had an `id="start-here"` anchor. Keep it resolvable so
                      old bookmarks/links to `/faq#start-here` still land on
                      Getting Started instead of nowhere. MCV only -- HLX
                      never had a `start-here` section. */}
                  {brand === 'mcv' && group.id === 'getting-started' && (
                    <div
                      id="start-here"
                      className={classes.sectionScrollMargin}
                      aria-hidden="true"
                    />
                  )}
                  <div className="max-w-3xl">
                    <h2
                      id={`${group.id}-heading`}
                      className="text-sm font-semibold uppercase tracking-wide text-primary-50"
                    >
                      {group.title}
                    </h2>
                    {group.description && (
                      <p className="mt-2 text-base leading-7 text-grey-300">{group.description}</p>
                    )}
                  </div>
                  <div className="mt-8 space-y-12">
                    {groupSections.map((section) => (
                      // One level below the group label above.
                      <FaqSectionView
                        key={section.id}
                        section={section}
                        headingLevel={3}
                        layout={layout}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            {/* [MCV-1592] Sections present as files but not listed in any nav
                group. `faqContent` deliberately appends these rather than
                dropping them, so that adding a JSON file and forgetting to
                reference it in faqGroups.ts degrades to "renders at the end"
                instead of "silently invisible". Walking the groups above would
                have quietly removed that guarantee -- there are none today,
                which is exactly why it would have gone unnoticed. */}
            {orphanedSections.length > 0 && (
              <div className="space-y-12">
                {orphanedSections.map((section) => (
                  <FaqSectionView key={section.id} section={section} layout={layout} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
